/* eslint-disable */
import React, { useEffect, useState } from "react"
import Column from "../../../../../components/atoms/table/Column.atom"
import MultiSelectTeamMemberFilter from "../../../../../components/compounds/MultiSelectTeamMemberFilter.compound"
import ColumnContent from "./ColumnContent.controller"
import { CustomTreeTable as TreeTable } from "../../../../../components/atoms/table/CustomTreeTable.atom"
import { roundReportData } from "../../../../../utils/report.utils"
import TeamChip from "../../../../../components/molecules/TeamChip.molecule"
import { UNIFIEDDATE_VALUES } from "../../../../../consts/CompoundRangeDatePicker"

/**
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursRowData} d
 * @returns {number}
 */
const memberSisuTotal = (d) => {
  if (d.sisuTime != null && d.sisuTime !== "") return Number(d.sisuTime)
  return Object.values(d.rangeData || {}).reduce(
    (acc, c) => acc + (Number(c?.sisuTime) || 0),
    0
  )
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursRowData} d
 * @returns {number}
 */
const memberBilledTotal = (d) => {
  if (d.billedTime != null && d.billedTime !== "") return Number(d.billedTime)
  return Object.values(d.rangeData || {}).reduce(
    (acc, c) => acc + (Number(c?.billedTime) || 0),
    0
  )
}

/**
 * Recomputes footer totals when the team-member name filter is active.
 *
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursTreeNode[]} data
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursReportTotals|undefined} totals
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursTableFilters} filters
 * @returns {import("./TeamHoursConsumptionTable.types").TeamHoursReportTotals|undefined}
 */
const calculateTotals = (data, totals, filters) => {
  let filteredData = []
  let newTotals = {
    sisuTime: 0,
    billedTime: 0,
  }
  const teamMemberData = data.flatMap((node) => node.children)

  if (filters["data.name"].value !== null) {
    if (totals.weekData !== undefined) {
      const weeksToConsider = Object.keys(totals.weekData)
      newTotals.weekData = {}
      weeksToConsider.forEach((week) => {
        newTotals.weekData[week] = {
          sisuTime: 0,
          billedTime: 0,
        }
      })
    } else if (totals.monthData !== undefined) {
      const monthsToConsider = Object.keys(totals.monthData)
      newTotals.monthData = {}
      monthsToConsider.forEach((month) => {
        newTotals.monthData[month] = {
          sisuTime: 0,
          billedTime: 0,
        }
      })
    }

    filteredData = teamMemberData.filter((node) =>
      filters["data.name"].value.includes(node.data.name)
    )

    filteredData.forEach((node) => {
      newTotals.sisuTime += memberSisuTotal(node.data)
      newTotals.billedTime += memberBilledTotal(node.data)
      if (totals.weekData !== undefined) {
        const weeksToConsider = Object.keys(node.data.rangeData)
        weeksToConsider.forEach((week) => {
          newTotals.weekData[week].sisuTime +=
            Number(node.data.rangeData[week].sisuTime) || 0
          newTotals.weekData[week].billedTime +=
            Number(node.data.rangeData[week].billedTime) || 0
        })
      } else if (totals.monthData !== undefined) {
        const monthsToConsider = Object.keys(node.data.rangeData)
        monthsToConsider.forEach((month) => {
          newTotals.monthData[month].sisuTime +=
            Number(node.data.rangeData[month].sisuTime) || 0
          newTotals.monthData[month].billedTime +=
            Number(node.data.rangeData[month].billedTime) || 0
        })
      }
    })

    newTotals.averageSisuTime = newTotals.sisuTime / (totals.rangeDuration ?? 1)
    newTotals.averageBilledTime =
      newTotals.billedTime / (totals.rangeDuration ?? 1)
    newTotals.averageTime = newTotals.averageSisuTime
    return newTotals
  } else {
    return totals
  }
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursTreeNode} rowData
 * @returns {import("react").ReactElement}
 */
const teamMemberNameTemplate = (rowData) => {
  if (rowData.data.code !== undefined) {
    return (
      <div className="flex align-items-center">
        <TeamChip teamTag={rowData.data.code} />
      </div>
    )
  } else if (rowData.data.teamMemberID !== undefined) {
    return (
      <div className="flex align-items-center">
        <span className="white-space-nowrap px-2">{rowData.data.name}</span>
      </div>
    )
  } else {
    return (
      <div className="flex align-items-center">
        <span className="white-space-nowrap px-2">N/A</span>
      </div>
    )
  }
}

/**
 * Clones report data and aggregates parent team rows from filtered children only.
 *
 * @param {string[]} activeFilters
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursReportPayload} originalData
 * @returns {import("./TeamHoursConsumptionTable.types").TeamHoursReportPayload}
 */
const calculateFilteredGroupHeadersTotals = (activeFilters, originalData) => {
  const clonedData = structuredClone(originalData)
  clonedData.data.forEach((el) => {
    Object.keys(el.data.rangeData).forEach((key) => {
      el.data.rangeData[key].sisuTime = 0
      el.data.rangeData[key].billedTime = 0
    })
    el.data.averageTime = 0
    el.data.averageSisuTime = 0
    el.data.averageBilledTime = 0
    el.data.billedTime = 0
    el.data.sisuTime = 0
  })

  clonedData.data.forEach((el) => {
    const childrenBeingFiltered = el.children.filter((child) => {
      return activeFilters.includes(child.data.name)
    })
    const hasChildrenBeingFiltered = childrenBeingFiltered.length > 0
    if (hasChildrenBeingFiltered) {
      childrenBeingFiltered.forEach((child) => {
        Object.keys(child.data.rangeData).forEach((key) => {
          el.data.rangeData[key].sisuTime +=
            Number(child.data.rangeData[key].sisuTime) || 0
          el.data.rangeData[key].billedTime +=
            Number(child.data.rangeData[key].billedTime) || 0
        })
        el.data.averageTime += Number(child.data.averageTime) || 0
        el.data.averageSisuTime +=
          Number(child.data.averageSisuTime ?? child.data.averageTime) || 0
        el.data.averageBilledTime += Number(child.data.averageBilledTime) || 0
        el.data.billedTime += memberBilledTotal(child.data)
        el.data.sisuTime += memberSisuTotal(child.data)
      })
    }
  })
  return clonedData
}

/**
 * Tree table for hours by team and member; exposes `reset()` via ref for parent filters.
 *
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursConsumptionTableProps} props
 * @param {import("react").Ref<import("./TeamHoursConsumptionTable.types").TeamHoursConsumptionTableRef>} ref
 * @returns {import("react").ReactElement}
 */
const TeamHoursConsumptionTable = React.forwardRef(
  (
    {
      reportData: originalData,
      showBilledHours,
      startYear,
      endYear,
      period,
      expandedKeys,
      setExpandedKeys,
    },
    ref
  ) => {
    const [filters, setFilters] = useState({
      "data.name": { value: null, matchMode: "in" },
    })

    const [reportData, setReportData] = useState(roundReportData(originalData))

    useEffect(() => {
      const activeFilters = filters["data.name"].value
      const isFilteringName = activeFilters !== null && activeFilters.length > 0

      if (isFilteringName) {
        const clonedData = calculateFilteredGroupHeadersTotals(
          activeFilters,
          originalData
        )

        setReportData(roundReportData(clonedData))
      } else {
        const clonedData = structuredClone(originalData)

        setReportData(roundReportData(clonedData))
      }
      setReportData((prev) => {
        const newData = { ...prev }
        newData.data = newData.data.filter((el) => el.children.length > 0)
        return newData
      })
    }, [filters, originalData])

    /** Clears the team-member column filter and collapses expanded rows. */
    const resetFilters = () => {
      collapseAll()
      setFilters({
        "data.name": { value: null, matchMode: "in" },
      })
    }

    const newRef = React.useRef()
    React.useImperativeHandle(
      ref,
      () => ({
        reset() {
          collapseAll()
          setFilters({
            "data.name": { value: null, matchMode: "in" },
          })
          newRef.current.reset()
        },
      }),
      []
    )

    const collapseAll = () => {
      setExpandedKeys({})
    }

    /** Expands every team row that has children (used when applying a name filter). */
    const expandAll = () => {
      const newExpandedKeys = {}
      reportData.data.forEach((node) => {
        newExpandedKeys[node.key] = true
      })
      setExpandedKeys(newExpandedKeys)
    }

    /**
     * @param {import("./TeamHoursConsumptionTable.types").TeamHoursTreeNode} node
     * @returns {boolean}
     */
    const teamMemberExpandCondition = (node) => {
      if (node?.children && node?.children.length > 0) return true
      else return false
    }

    /**
     * Ordered week or month keys from API totals metadata.
     *
     * @param {import("./TeamHoursConsumptionTable.types").TeamHoursReportPayload} reportDataTemporal
     * @returns {(string|number)[]}
     */
    const rangeArray = (reportDataTemporal) => {
      if (
        reportDataTemporal.totals?.monthsToConsider?.length !== undefined &&
        reportDataTemporal.totals?.monthsToConsider?.length !== 0
      )
        return reportDataTemporal.totals.monthsToConsider
      else if (
        reportDataTemporal.totals?.weeksToConsider?.length !== undefined &&
        reportDataTemporal.totals?.weeksToConsider?.length !== 0
      )
        return reportDataTemporal.totals.weeksToConsider
      else return []
    }

    const noData = reportData.data.every((node) => node.children.length === 0)

    return (
      <>
        <TreeTable
          ref={newRef}
          value={reportData.data}
          className={noData ? "disabled-filters" : ""}
          expandedKeys={expandedKeys}
          onToggle={(e) => setExpandedKeys(e.value)}
          removableSort
          filters={filters}
          globalFilterFields={["data.name"]}
          expanderConditions={(node) => teamMemberExpandCondition(node)}
          allowExpandingChildren
          scrollable
          scrollHeight="100%"
          frozenExpandedColumn
        >
          <Column
            body={teamMemberNameTemplate}
            style={{
              width: "215px",
              minWidth: "215px",
              padding: "7px 7px 7px 0px",
            }}
            header={() => <p style={{ marginLeft: -45 }}>Equipos</p>}
            footer={() => (
              <p style={{ marginLeft: -45 }}>
                Total por{" "}
                {period === UNIFIEDDATE_VALUES.RANGEMONTH ? "mes" : "semana"}
              </p>
            )}
            filter
            frozen
            filterField="data.name"
            filterElement={(options) => (
              <MultiSelectTeamMemberFilter
                key={reportData.data.length}
                options={options}
                data={reportData.data.filter(
                  (node) => node.children.length > 0
                )}
              />
            )}
            showFilterMenuOptions={false}
            showFilterMatchModes={false}
            filterMatchMode={"in"}
            onFilterApplyClick={(e) => {
              if (!e.constraints?.value) {
                collapseAll()
              } else {
                expandAll()
                setFilters({
                  "data.name": { value: e.constraints?.value, matchMode: "in" },
                })
              }
            }}
            onFilterClear={resetFilters}
          />
          {ColumnContent(
            rangeArray(reportData),
            startYear,
            endYear,
            period,
            calculateTotals(reportData.data, reportData.totals, filters),
            showBilledHours
          )?.map((column, index) => (
            <Column key={index} {...column} />
          ))}
        </TreeTable>
      </>
    )
  }
)

export default TeamHoursConsumptionTable
